import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { formatDateTime } from "../../lib/format";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import Hint from "./Hint";

const ALASAN_MIN = 10;

/**
 * K217 (keputusan Owner 17 Okt 2026): tautan DOKU (tanda jadi, uang muka, pelunasan) menawarkan QRIS dan Virtual
 * Account. Owner menyalakan / mematikan tiap cara bayar di sini, tanpa deploy — alasan wajib, tercatat. Berlaku untuk
 * tautan BARU; tautan lama yang pilihannya berbeda tidak diberikan lagi. Minimal satu cara bayar tetap menyala
 * (server menolak mematikan yang terakhir). Hanya Owner (layar Pengaturan).
 */
export default function KartuMetodeDoku() {
  const [data, setData] = useState(null);
  const [alasan, setAlasan] = useState({});
  const [sibuk, setSibuk] = useState(null);

  const muat = useCallback(async () => {
    try { const r = await api.get("/payments/metode-doku"); setData(r.data); }
    catch { setData({ galat: true }); }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  if (!data) return null;
  if (data.galat) {
    return (
      <Card className="p-4 text-sm text-muted-foreground" data-testid="kartu-metode-doku">
        Pilihan cara bayar DOKU belum bisa dimuat. Coba muat ulang halaman.
      </Card>
    );
  }
  const nyalaSemua = (data.metode || []).filter((m) => m.nyala);
  const ubah = async (m, nyala) => {
    setSibuk(m.metode);
    try {
      await api.post("/payments/metode-doku", { metode: m.metode, nyala, alasan: alasan[m.metode] || "" });
      toast.success(nyala ? `${m.label} dinyalakan untuk tautan DOKU baru.`
                          : `${m.label} dimatikan — tautan DOKU baru tidak menawarkannya.`);
      setAlasan((a) => ({ ...a, [m.metode]: "" }));
      muat();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengubah cara bayar DOKU"));
    } finally {
      setSibuk(null);
    }
  };

  return (
    <Card className="p-6 space-y-3" data-testid="kartu-metode-doku">
      <div className="text-sm font-medium flex items-center gap-1">
        Cara bayar DOKU
        <Hint>
          Berlaku untuk tautan pembayaran DOKU yang BARU (tanda jadi, uang muka, pelunasan). Tiap cara bayar yang
          menyala wajib punya alamat pemberitahuan pembayaran di Back Office DOKU.
        </Hint>
      </div>
      {!data.terbaca && (
        <p className="text-sm text-danger" data-testid="metode-doku-tidak-terbaca">
          Pengaturan cara bayar belum terbaca — tautan baru sementara hanya menawarkan QRIS.
        </p>
      )}
      <p className="text-sm text-muted-foreground" data-testid="metode-doku-berlaku">
        Ditawarkan ke customer sekarang: {(data.berlaku || []).map((m) => m.label).join(", ") || "QRIS"}
      </p>
      <ul className="space-y-3">
        {(data.metode || []).map((m) => {
          const terakhir = m.nyala && nyalaSemua.length <= 1;
          const isi = alasan[m.metode] || "";
          return (
            <li key={m.metode} className="border-t pt-3 space-y-2" data-testid={`metode-doku-${m.metode}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-medium">{m.label}</span>
                <Badge variant={m.nyala ? "default" : "secondary"}>{m.nyala ? "Menyala" : "Mati"}</Badge>
              </div>
              {m.alasan ? (
                <div className="text-xs text-muted-foreground">
                  {m.alasan}{m.diubah_at ? ` (${formatDateTime(m.diubah_at)})` : ""}
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Input className="max-w-sm" placeholder="Alasan (minimal 10 huruf)" value={isi}
                       onChange={(e) => setAlasan((a) => ({ ...a, [m.metode]: e.target.value }))}
                       data-testid={`metode-doku-alasan-${m.metode}`} />
                <Button size="sm" variant={m.nyala ? "destructive" : "default"}
                        data-testid={`metode-doku-ubah-${m.metode}`}
                        disabled={sibuk !== null || isi.trim().length < ALASAN_MIN || terakhir || !m.ada}
                        onClick={() => ubah(m, !m.nyala)}>
                  {sibuk === m.metode ? "Menyimpan…" : m.nyala ? "Matikan" : "Nyalakan"}
                </Button>
                {terakhir ? (
                  <span className="text-xs text-muted-foreground">Minimal satu cara bayar tetap menyala.</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
